// src/components/HR/components/Pages/Employees.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import {
  User, Edit3, Trash2, Eye, Award, FileText, CreditCard,
  UploadCloud, Save, UserPlus, Search, Filter, Briefcase,
  AlertTriangle, Users, Star, Download, CheckCircle, Layers
} from 'lucide-react';
import Card from '../Card';
import Button from '../UI/Button';
import Modal from '../UI/Modal';
import { hrApi } from '../../../api';
import { getOrgId } from '../../utils/auth';
import { toRatingHistorySeries } from '../../../../utils/workerRatings';
import { useMasterData } from '../../../../utils/useMasterData';

const normalizeText = (value) => String(value || '').trim().toLowerCase();

// ── Skeleton shimmer block ──────────────────────────────────────────────────
const Shimmer = ({ width = '100%', height = 16, radius = 6, style = {} }) => (
  <span className="skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

const TableRowSkeleton = ({ cols = 8 }) => (
  <tr>
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} style={{ padding: '14px 16px' }}>
        {i === 1
          ? <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <Shimmer width="70%" height={13} />
            <Shimmer width="45%" height={11} />
          </div>
          : <Shimmer height={13} width={i === cols - 1 ? 60 : '60%'} />
        }
      </td>
    ))}
  </tr>
);

// ── Spinner ─────────────────────────────────────────────────────────────────
const Spinner = ({ size = 'md', label = 'Loading…', color = '#6366f1' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '32px 16px' }}>
    <span className={`spinner-ring${size === 'sm' ? ' sm' : ''}`} style={{ borderTopColor: color }} />
    {label && <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>{label}</span>}
  </div>
);

// ── Inline button spinner ────────────────────────────────────────────────────
const BtnSpinner = () => (
  <span className="spinner-ring sm" style={{ borderTopColor: 'white', verticalAlign: 'middle' }} />
);

// ── Star Rating display ─────────────────────────────────────────────────────
const StarRating = ({ value }) => {
  const safe = Math.max(0, Math.min(5, Number(value || 0)));
  return (
    <span title={`Rating: ${safe.toFixed(1)}/5`} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.max(0, Math.min(1, safe - i));
        return (
          <svg key={i} width={16} height={16} viewBox="0 0 24 24" fill={fill >= 1 ? '#f59e0b' : 'none'} stroke={fill > 0 ? '#f59e0b' : '#d1d5db'} strokeWidth="2">
            <path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2Z" />
            {fill > 0 && fill < 1 && (
              <defs>
                <clipPath id={`star-clip-${i}`}>
                  <rect x="0" y="0" width={24 * fill} height="24" />
                </clipPath>
              </defs>
            )}
            {fill > 0 && fill < 1 && (
              <path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2Z" fill="#f59e0b" clipPath={`url(#star-clip-${i})`} />
            )}
          </svg>
        );
      })}
      <span style={{ fontSize: 12, color: '#475569', fontWeight: 700, marginLeft: 4 }}>{safe.toFixed(1)}</span>
    </span>
  );
};

// ── Employees Page ──────────────────────────────────────────────────────────
const Employees = ({ departments = [], addToast, userRole, canWrite: canWriteProp }) => {
  const { getOptions } = useMasterData(['DEPARTMENT', 'DOC_TYPE', 'WORKER_ROLE']);
  const masterDepartmentOptions = getOptions('DEPARTMENT').map((option) => ({ id: option.value, name: option.label }));
  const docTypeOptions = getOptions('DOC_TYPE').map((option) => ({ value: option.value, label: option.label }));
  const workerRoleOptions = getOptions('WORKER_ROLE');
  const org_id = getOrgId();
  const normalizedUserRole = String(userRole || '').trim().toUpperCase();
  const canManageWorkers =
    Boolean(canWriteProp) ||
    ['ADMIN', 'ORG_ADMIN', 'SUPER_ADMIN', 'HR_MANAGER', 'HR_ADMIN', 'HR_EXECUTIVE', 'HR_STAFF'].includes(normalizedUserRole);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [typeFilter, setTypeFilter] = useState('Worker');
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('All');
  const [roleFilter, setRoleFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const [viewingEmp, setViewingEmp] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Worker view data
  const [workerLeave, setWorkerLeave] = useState([]);
  const [workerPerf, setWorkerPerf] = useState(null);
  const [workerPayslips, setWorkerPayslips] = useState([]);
  const [workerStages, setWorkerStages] = useState([]);
  const [workerRatingHistory, setWorkerRatingHistory] = useState([]);
  const [viewTab, setViewTab] = useState('overview');
  const [viewLoading, setViewLoading] = useState(false);
  const [ratingHistoryLoading, setRatingHistoryLoading] = useState(false);
  const [bonusFilter, setBonusFilter] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staffViewTab, setStaffViewTab] = useState('overview');
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [isUploadDocModalOpen, setIsUploadDocModalOpen] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docPreviewUrl, setDocPreviewUrl] = useState('');
  const [stageOptions, setStageOptions] = useState([]);
  const [orgBranches, setOrgBranches] = useState([]);
  const [docForm, setDocForm] = useState({
    document_type: 'ID_PROOF',
    issue_date: '',
    expiry_date: '',
    notes: '',
    file: null,
  });

  const initialFormState = {
    firstName: '', lastName: '', email: '', phone: '', dob: '', gender: 'Male',
    id: '', workerCode: 'Auto generated after save', role: '', dept: '', branch: '', stage: '', joiningDate: '',
    employmentType: 'Daily', experience: 0, skills: '', wage: 0,
    bank: { holder: '', bank: '', acc: '', ifsc: '', branch: '', accountType: '' },
    address: { line1: '', line2: '', city: '', state: '', pincode: '' },
    document: { type: '', number: '', nameOnDoc: '' },
    status: 'Active', rating: 3, remarks: ''
  };
  const [formData, setFormData] = useState(initialFormState);
  const departmentOptions = departments.length ? departments : masterDepartmentOptions;

  const branchOptions = useMemo(() => {
    const seen = new Map();
    orgBranches.forEach((branch) => {
      const branchId = String(branch.id || '');
      const branchName = branch.name || branch.code || `Branch ${branchId}`;
      if (branchId && !seen.has(branchId)) {
        seen.set(branchId, { id: branchId, name: branchName });
      }
    });
    departmentOptions.forEach((dept) => {
      const branchId = dept.branch_id || dept.branchId || dept.branch || '';
      const branchName = dept.branch_name || dept.branchName || dept.branch || `Branch ${branchId}`;
      if (branchId && !seen.has(String(branchId))) {
        seen.set(String(branchId), { id: String(branchId), name: branchName });
      }
    });
    return Array.from(seen.values());
  }, [departmentOptions, orgBranches]);

  const resolveBranchIdForDepartment = useCallback((deptId) => {
    const dept = departmentOptions.find((item) => String(item.id) === String(deptId));
    return String(dept?.branch_id || dept?.branchId || dept?.branch || branchOptions[0]?.id || '');
  }, [departmentOptions, branchOptions]);

  const resolveBranchNameForDepartment = useCallback((deptId) => {
    const dept = departmentOptions.find((item) => String(item.id) === String(deptId));
    return dept?.branch_name || dept?.branchName || dept?.branch || branchOptions.find((item) => item.id === String(resolveBranchIdForDepartment(deptId)))?.name || 'Auto from department';
  }, [departmentOptions, branchOptions, resolveBranchIdForDepartment]);


  const getExpiryBadge = (status) => {
    const key = String(status || '').toUpperCase();
    if (key === 'EXPIRED') {
      return { label: 'EXPIRED', bg: '#fee2e2', color: '#991b1b' };
    }
    if (key === 'EXPIRING_SOON') {
      return { label: 'EXPIRING SOON', bg: '#ffedd5', color: '#9a3412' };
    }
    if (key === 'NO_EXPIRY') {
      return { label: 'NO EXPIRY', bg: '#e2e8f0', color: '#334155' };
    }
    return { label: 'VALID', bg: '#dcfce7', color: '#166534' };
  };

  // ── API ──────────────────────────────────────────────────────────────────
  const fetchEmployees = useCallback(async (type) => {
    setLoading(true);
    setEmployees([]);
    try {
      const response = await hrApi.get(`/orgs/${org_id}/employees`, { params: { type } });
      const items = (response.data.items || []).filter(e => e.type === type);
      setEmployees(items);
    } catch {
      addToast('Failed to load employees', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast, org_id]);

  useEffect(() => {
    fetchEmployees(typeFilter);
    setDeptFilter('All');
    setRoleFilter('');
    setStageFilter('');
    setSearch('');
    setStatusFilter('All');
    setBonusFilter(false);
  }, [fetchEmployees, typeFilter]);

  useEffect(() => {
    const fetchStageOptions = async () => {
      try {
        const res = await hrApi.get('/job-cards/stages/catalog');
        const groups = res.data?.data || {};
        const flattened = Object.values(groups).flatMap((group) => group?.unique || []);
        const deduped = flattened.filter((item, index, arr) =>
          index === arr.findIndex((row) => String(row.stage_code) === String(item.stage_code))
        );
        setStageOptions(deduped);
      } catch {
        setStageOptions([]);
      }
    };
    fetchStageOptions();
  }, []);

  useEffect(() => {
    const fetchOrgBranches = async () => {
      try {
        const res = await hrApi.get('/branches/organization/my-branches');
        const rows = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setOrgBranches(rows);
      } catch {
        setOrgBranches([]);
      }
    };
    fetchOrgBranches();
  }, []);

  const fetchEmployeeDocuments = async (employeeId) => {
    if (!employeeId) return;
    setDocumentsLoading(true);
    try {
      const res = await hrApi.get(`/hr/employees/${employeeId}/documents`);
      setDocuments(res.data?.data || []);
    } catch {
      setDocuments([]);
      addToast('Failed to load employee documents', 'error');
    } finally {
      setDocumentsLoading(false);
    }
  };

  const resetDocForm = () => {
    setDocForm({
      document_type: 'ID_PROOF',
      issue_date: '',
      expiry_date: '',
      notes: '',
      file: null,
    });
    setDocPreviewUrl('');
  };

  const handleUploadDocument = async () => {
    if (!viewingEmp?.db_id || !docForm.file) {
      addToast('Please select a document file', 'error');
      return;
    }
    setUploadingDoc(true);
    try {
      const data = new FormData();
      data.append('file', docForm.file);
      data.append('document_type', docForm.document_type);
      if (docForm.issue_date) data.append('issue_date', docForm.issue_date);
      if (docForm.expiry_date) data.append('expiry_date', docForm.expiry_date);
      if (docForm.notes) data.append('notes', docForm.notes);
      await hrApi.post(`/hr/employees/${viewingEmp.db_id}/documents`, data);
      addToast('Document uploaded successfully', 'success');
      setIsUploadDocModalOpen(false);
      resetDocForm();
      fetchEmployeeDocuments(viewingEmp.db_id);
    } catch (error) {
      addToast(error.response?.data?.message || 'Document upload failed', 'error');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const res = await hrApi.get(doc.download_url, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || 'employee_document';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      addToast('Unable to download document', 'error');
    }
  };

  // ── Unique roles for filter dropdown ─────────────────────────────────────
  const availableRoles = useMemo(() => {
    const roleMap = new Map();
    employees.forEach((employee) => {
      const rawRole = employee.role || employee.designation || employee.job_title || '';
      const key = normalizeText(rawRole);
      if (key && !roleMap.has(key)) {
        roleMap.set(key, String(rawRole).trim());
      }
    });
    return Array.from(roleMap.values()).sort((a, b) => a.localeCompare(b));
  }, [employees]);

  const availableBranches = useMemo(() => {
    const branchMap = new Map();
    employees.forEach((employee) => {
      const rawBranch = employee.branch_name || employee.branch || '';
      const key = normalizeText(rawBranch);
      if (key && !branchMap.has(key)) {
        branchMap.set(key, String(rawBranch).trim());
      }
    });
    return Array.from(branchMap.values()).sort((a, b) => a.localeCompare(b));
  }, [employees]);

  const availableDepts = useMemo(() => {
    if (typeFilter === 'Worker') {
      const deptMap = new Map();
      departmentOptions.forEach((department) => {
        const rawDept = department.name || department.department_name || '';
        const key = normalizeText(rawDept);
        if (key && !deptMap.has(key)) {
          deptMap.set(key, String(rawDept).trim());
        }
      });
      employees.forEach((employee) => {
        const rawDept = employee.department || employee.department_name || '';
        const key = normalizeText(rawDept);
        if (key && !deptMap.has(key)) {
          deptMap.set(key, String(rawDept).trim());
        }
      });
      return Array.from(deptMap.values()).sort((a, b) => a.localeCompare(b));
    }
    return availableBranches;
  }, [employees, typeFilter, availableBranches, departmentOptions]);

  const availableStatuses = useMemo(() => {
    const statusMap = new Map();
    employees.forEach((employee) => {
      const rawStatus = employee.status || '';
      const key = normalizeText(rawStatus);
      if (key && !statusMap.has(key)) {
        statusMap.set(key, String(rawStatus).trim());
      }
    });
    return Array.from(statusMap.values()).sort((a, b) => a.localeCompare(b));
  }, [employees]);

  const availableStageTypes = useMemo(() => {
    if (typeFilter !== 'Worker') return [];
    const stageMap = new Map();
    stageOptions.forEach((stage) => {
      const rawStage = stage.stage_name || stage.name || '';
      const key = normalizeText(rawStage);
      if (key && !stageMap.has(key)) {
        stageMap.set(key, String(rawStage).trim());
      }
    });
    employees.forEach((employee) => {
      const rawStage = employee.role || employee.stage || employee.stage_name || '';
      const key = normalizeText(rawStage);
      if (key && !stageMap.has(key)) {
        stageMap.set(key, String(rawStage).trim());
      }
    });
    return Array.from(stageMap.values()).sort((a, b) => a.localeCompare(b));
  }, [employees, stageOptions, typeFilter]);

  // ── Filtered list ────────────────────────────────────────────────────────
  useEffect(() => {
    if (deptFilter !== 'All' && !availableDepts.some((value) => normalizeText(value) === normalizeText(deptFilter))) {
      setDeptFilter('All');
    }
  }, [availableDepts, deptFilter]);

  useEffect(() => {
    if (roleFilter && !availableRoles.some((value) => normalizeText(value) === normalizeText(roleFilter))) {
      setRoleFilter('');
    }
  }, [availableRoles, roleFilter]);

  useEffect(() => {
    if (stageFilter && !availableStageTypes.some((value) => normalizeText(value) === normalizeText(stageFilter))) {
      setStageFilter('');
    }
  }, [availableStageTypes, stageFilter]);

  useEffect(() => {
    if (statusFilter !== 'All' && !availableStatuses.some((value) => normalizeText(value) === normalizeText(statusFilter))) {
      setStatusFilter('All');
    }
  }, [availableStatuses, statusFilter]);

  const filtered = useMemo(() => {
    const searchNeedle = normalizeText(search);
    const filteredItems = employees.filter(e => {
      // Hard filter: only show items matching the selected type
      if (normalizeText(e.type) && normalizeText(e.type) !== normalizeText(typeFilter)) return false;

      const fullName = typeFilter === 'Worker'
        ? `${e.firstName || ''} ${e.lastName || ''}`
        : (e.name || `${e.firstName || ''} ${e.lastName || ''}`);
      const searchableFields = [
        fullName,
        e.id,
        e.email,
        e.phone,
        e.role,
        e.designation,
        e.job_title,
        e.department,
        e.department_name,
        e.branch_name,
        e.branch,
      ]
        .map(normalizeText)
        .filter(Boolean);
      const matchSearch = !searchNeedle || searchableFields.some((value) => value.includes(searchNeedle));
      const groupCol = typeFilter === 'Worker'
        ? (e.department || e.department_name || '')
        : (e.branch_name || e.branch || '');
      const matchGroup = deptFilter === 'All' || normalizeText(groupCol) === normalizeText(deptFilter);
      const matchStatus = statusFilter === 'All' || normalizeText(e.status) === normalizeText(statusFilter);
      const matchBonus = !bonusFilter || Number(e.rating || 0) >= 4;
      const roleValue = e.role || e.designation || e.job_title || '';
      const matchRole = !roleFilter || normalizeText(roleValue) === normalizeText(roleFilter);
      const stageValue = e.stage || e.stage_name || e.role || '';
      const matchStage = !stageFilter || normalizeText(stageValue) === normalizeText(stageFilter);
      return matchSearch && matchGroup && matchStatus && matchBonus && matchRole && matchStage;
    });

    const getSearchScore = (employee) => {
      if (!searchNeedle) return 0;
      const fullName = normalizeText(typeFilter === 'Worker'
        ? `${employee.firstName || ''} ${employee.lastName || ''}`
        : (employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`));
      const employeeId = normalizeText(employee.id);
      if (fullName === searchNeedle || employeeId === searchNeedle) return 3;
      if (fullName.startsWith(searchNeedle) || employeeId.startsWith(searchNeedle)) return 2;
      if (fullName.includes(searchNeedle) || employeeId.includes(searchNeedle)) return 1;
      return 0;
    };

    return filteredItems.sort((a, b) => {
      const scoreDiff = getSearchScore(b) - getSearchScore(a);
      if (scoreDiff !== 0) return scoreDiff;
      return new Date(b.joiningDate || 0) - new Date(a.joiningDate || 0);
    });
  }, [employees, search, deptFilter, statusFilter, bonusFilter, roleFilter, stageFilter, typeFilter]);

  const visibleRows = filtered;
  const tableInstanceKey = [
    typeFilter,
    search,
    deptFilter,
    roleFilter,
    stageFilter,
    statusFilter,
    bonusFilter ? 'bonus' : 'all',
    visibleRows.length,
  ].join('|');

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleOpenAdd = () => {
    setEditingEmp(null);
    setSelectedFile(null);
    const defaultDept = departmentOptions[0]?.id ? String(departmentOptions[0].id) : '';
    const defaultBranch = branchOptions[0]?.id || '';
    setFormData({
      ...initialFormState,
      dept: defaultDept,
      branch: resolveBranchIdForDepartment(defaultDept) || defaultBranch,
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (emp) => {
    setEditingEmp(emp);
    setSelectedFile(null);
    setFormData({
      ...initialFormState,
      ...emp,
      workerCode: emp.id || emp.worker_code || 'Auto generated after save',
      dept: String(emp.department_id || ''),
      branch: String(emp.branch_id || resolveBranchIdForDepartment(emp.department_id || '')),
      bank: emp.bank || initialFormState.bank,
      address: emp.address || initialFormState.address,
      document: emp.document || initialFormState.document,
    });
    setIsModalOpen(true);
  };

  const handleOpenView = async (emp) => {
    if (emp.type === 'Staff' || typeFilter === 'Staff') {
      try {
        setWorkerRatingHistory([]);
        setRatingHistoryLoading(false);
        setStaffViewTab('overview');
        resetDocForm();
        const res = await hrApi.get(`/orgs/${org_id}/employees/staff/${emp.db_id}`);
        setViewingEmp({ ...res.data, _viewType: 'staff' });
        fetchEmployeeDocuments(emp.db_id);
        setIsViewModalOpen(true);
      } catch {
        addToast('Could not fetch staff details', 'error');
      }
      return;
    }
    // Worker view — load all 4 apis
    setViewingEmp({ ...emp, _viewType: 'worker' });
    setViewTab('overview');
    setViewLoading(true);
    setRatingHistoryLoading(true);
    setIsViewModalOpen(true);
    try {
      const [leaveRes, perfRes, payRes, stagesRes, historyRes] = await Promise.allSettled([
        hrApi.get(`/orgs/${org_id}/employees/workers/${emp.db_id}/leave-balance`),
        hrApi.get(`/orgs/${org_id}/employees/workers/${emp.db_id}/performance-summary`),
        hrApi.get(`/orgs/${org_id}/employees/workers/${emp.db_id}/payslip-history`),
        hrApi.get(`/orgs/${org_id}/employees/workers/${emp.db_id}/assigned-stages`),
        hrApi.get(`/orgs/${org_id}/hr/workers/${emp.db_id}/rating-history`, { params: { months: 12 } }),
      ]);
      setWorkerLeave(leaveRes.status === 'fulfilled' ? leaveRes.value.data?.data || [] : []);
      setWorkerPerf(perfRes.status === 'fulfilled' ? perfRes.value.data : null);
      setWorkerPayslips(payRes.status === 'fulfilled' ? payRes.value.data?.data || [] : []);
      setWorkerStages(stagesRes.status === 'fulfilled' ? stagesRes.value.data?.data || [] : []);
      setWorkerRatingHistory(historyRes.status === 'fulfilled' ? historyRes.value.data?.data || [] : []);
    } catch {
      addToast('Could not load some worker details', 'error');
    } finally {
      setViewLoading(false);
      setRatingHistoryLoading(false);
    }
  };

  const handleDeleteClick = (emp) => {
    setDeleteTarget(emp);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async (soft) => {
    if (!deleteTarget) return;
    try {
      await hrApi.delete(`/orgs/${org_id}/employees/workers/${deleteTarget.db_id}`, { params: { soft } });
      addToast(soft ? `${deleteTarget.firstName} marked as Inactive` : `${deleteTarget.firstName} permanently deleted`, 'success');
      fetchEmployees(typeFilter);
    } catch {
      addToast('Delete operation failed', 'error');
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleSave = async () => {
    if (!formData.firstName || !formData.phone || !formData.dept) {
      addToast('Please fill required fields: First Name, Phone, and Department', 'error');
      return;
    }
    setSaving(true);
    try {
      const resolvedBranchId = formData.branch || resolveBranchIdForDepartment(formData.dept) || '1';
      if (editingEmp) {
        await hrApi.put(`/orgs/${org_id}/employees/workers/${editingEmp.db_id}`, {
          ...formData,
          department_id: formData.dept,
          branch_id: resolvedBranchId,
          role: formData.role,
          remarks: formData.remarks,
          bank_account: formData.bank.acc,
          bank_name: formData.bank.bank,
          bank_holder: formData.bank.holder,
          bank_ifsc: formData.bank.ifsc,
          bank_branch: formData.bank.branch,
          bank_account_type: formData.bank.accountType,
          address_line1: formData.address.line1,
          address_line2: formData.address.line2,
          city: formData.address.city,
          state: formData.address.state,
          pincode: formData.address.pincode
        });
        addToast('Worker updated successfully', 'success');
      } else {
        const data = new FormData();
        data.append('firstName', formData.firstName);
        data.append('lastName', formData.lastName);
        data.append('phone', formData.phone);
        data.append('wage', formData.wage);
        data.append('department_id', formData.dept);
        data.append('branch_id', resolvedBranchId);
        data.append('role', formData.role);
        data.append('skills', formData.skills);
        data.append('experience', formData.experience);
        data.append('rating', formData.rating);
        data.append('remarks', formData.remarks);
        if (selectedFile) data.append('document', selectedFile);
        data.append('bank_account', formData.bank.acc);
        data.append('bank_name', formData.bank.bank);
        data.append('bank_holder', formData.bank.holder);
        data.append('bank_ifsc', formData.bank.ifsc);
        data.append('bank_branch', formData.bank.branch);
        data.append('bank_account_type', formData.bank.accountType);
        data.append('doc_type', formData.document.type);
        data.append('doc_number', formData.document.number);
        data.append('name_on_document', formData.document.nameOnDoc);
        data.append('address_line1', formData.address.line1);
        data.append('address_line2', formData.address.line2);
        data.append('city', formData.address.city);
        data.append('state', formData.address.state);
        data.append('pincode', formData.address.pincode);
        await hrApi.post(`/orgs/${org_id}/employees/workers`, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        addToast('New worker added successfully', 'success');
      }
      setIsModalOpen(false);
      setSelectedFile(null);
      setFormData(initialFormState);
      fetchEmployees(typeFilter);
    } catch (error) {
      addToast(error.response?.data?.error || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const set = (field, val) => setFormData(f => ({ ...f, [field]: val }));
  const setBank = (field, val) => setFormData(f => ({ ...f, bank: { ...f.bank, [field]: val } }));
  const setDoc = (field, val) => setFormData(f => ({ ...f, document: { ...f.document, [field]: val } }));
  const setAddr = (field, val) => setFormData(f => ({ ...f, address: { ...f.address, [field]: val } }));

  if (!org_id) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
      <div className="skeleton" style={{ width: 200, height: 20, margin: '0 auto' }} />
    </div>
  );

  const colCount = typeFilter === 'Worker' ? 9 : 8;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Top Bar ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>
            {typeFilter === 'Worker' ? 'Daily Workers' : 'Staff Members'}
          </h2>
          <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>
            {typeFilter === 'Worker'
              ? 'Manage daily-wage workers — add, edit, track ratings'
              : 'View system users with assigned roles and branch access'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Type toggle */}
          <div style={{ display: 'flex', background: '#f1f5f9', padding: 3, borderRadius: 8 }}>
            {[['Worker', 'Daily Workers'], ['Staff', 'Staff / Users']].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setTypeFilter(val)}
                style={{
                  padding: '7px 18px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                  background: typeFilter === val ? 'white' : 'transparent',
                  color: typeFilter === val ? '#1e293b' : '#64748b',
                  cursor: 'pointer',
                  boxShadow: typeFilter === val ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {typeFilter === 'Worker' && (
            <Button
              variant="primary"
              icon={UserPlus}
              onClick={handleOpenAdd}
              title="Add a new worker"
            >
              Add New Worker
            </Button>
          )}
        </div>
      </div>

      {/* ── Table Card ──────────────────────────────────────────────── */}
      <Card>
        {loading && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 16px', marginBottom: 4,
            background: '#f0f4ff', borderRadius: 8,
            fontSize: 13, color: '#4f46e5', fontWeight: 500
          }}>
            <span className="spinner-ring sm" style={{ borderTopColor: '#4f46e5' }} />
            Fetching employee records…
          </div>
        )}
        {/* Filter Row */}
        <div style={{
          display: 'flex', gap: 8, paddingBottom: 16,
          borderBottom: '1px solid #f1f5f9', marginBottom: 16,
          alignItems: 'center', flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#94a3b8', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
            <Filter size={15} /> Filters:
          </div>
          {/* Search */}
          <div style={{ position: 'relative', flex: '1 1 180px', minWidth: 150 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text" placeholder={typeFilter === 'Worker' ? 'Search worker, phone, code, stage...' : 'Search staff, email, ID...'}
              className="search-input" style={{ paddingLeft: 32, width: '100%' }}
              value={search} onChange={e => setSearch(e.target.value)}
            />
          </div>
          {/* Dept / Branch */}
          <select className="form-select" style={{ flex: '0 0 auto', minWidth: 140 }} value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
            <option value="All">{typeFilter === 'Worker' ? 'All Departments' : 'All Branches'}</option>
            {availableDepts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          {/* Role / Designation */}
          <select className="form-select" style={{ flex: '0 0 auto', minWidth: 150 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
            <option value="">{typeFilter === 'Worker' ? 'All Worker Types' : 'All Designations'}</option>
            {availableRoles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          {typeFilter === 'Worker' && (
            <select className="form-select" style={{ flex: '0 0 auto', minWidth: 170 }} value={stageFilter} onChange={e => setStageFilter(e.target.value)}>
              <option value="">All Stages</option>
              {availableStageTypes.map(stage => <option key={stage} value={stage}>{stage}</option>)}
            </select>
          )}
          {/* Status */}
          <select className="form-select" style={{ flex: '0 0 auto', minWidth: 130 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Statuses</option>
            {availableStatuses.map(status => <option key={status} value={status}>{status}</option>)}
          </select>
          {/* Bonus Eligible toggle */}
          {typeFilter === 'Worker' && (
            <button
              onClick={() => setBonusFilter(f => !f)}
              style={{
                padding: '7px 14px', borderRadius: 8, border: bonusFilter ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                background: bonusFilter ? '#fffbeb' : '#f8fafc', color: bonusFilter ? '#d97706' : '#64748b',
                fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                transition: 'all 0.2s'
              }}
            >
              <Star size={13} fill={bonusFilter ? '#f59e0b' : 'none'} /> Bonus Eligible
            </button>
          )}
          {/* Count badge */}
          {!loading && (
            <div style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {filtered.length} record{filtered.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="table-wrapper">
          <table key={tableInstanceKey}>
            <thead>
              <tr>
                <th>ID</th>
                <th style={{ textAlign: 'left', paddingLeft: 16 }}>Name</th>
                {typeFilter === 'Worker' ? <th>Department</th> : <th>Branch</th>}
                <th>Role</th>
                {typeFilter === 'Worker' && <th>Daily Wage</th>}
                {typeFilter === 'Worker' && <th>Rating</th>}
                {typeFilter === 'Staff' && <th>Email</th>}
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => <TableRowSkeleton key={i} cols={colCount} />)
                : visibleRows.length === 0
                  ? (
                    <tr>
                      <td colSpan={colCount} style={{ padding: '48px 16px', textAlign: 'center' }}>
                        <Users size={36} style={{ color: '#e2e8f0', marginBottom: 10 }} />
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#94a3b8' }}>No employees found</div>
                        <div style={{ fontSize: 13, color: '#cbd5e1', marginTop: 4 }}>Try adjusting your filters</div>
                      </td>
                    </tr>
                  )
                  : visibleRows.map((emp, index) => (
                    <tr
                      key={`${emp.db_id || emp.id || index}-${normalizeText(emp.firstName || emp.name)}-${normalizeText(emp.lastName)}-${normalizeText(emp.phone)}`}
                      style={{ opacity: emp.status === 'Inactive' ? 0.55 : 1, transition: 'opacity 0.2s' }}
                    >
                      <td style={{ color: '#94a3b8', fontSize: 12, fontFamily: 'monospace' }}>{emp.id}</td>
                      <td style={{ textAlign: 'left', paddingLeft: 16 }}>
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>
                          {typeFilter === 'Worker' ? `${emp.firstName} ${emp.lastName || ''}` : (emp.name || `${emp.firstName} ${emp.lastName || ''}`)}
                        </div>
                        {emp.phone && <div style={{ fontSize: 11, color: '#94a3b8' }}>{emp.phone}</div>}
                      </td>
                      {typeFilter === 'Worker' ? (
                        <td>{emp.department || '—'}</td>
                      ) : (
                        <td>{emp.branch_name || '—'}</td>
                      )}
                      <td>
                        <span style={{
                          display: 'inline-flex', padding: '3px 10px', borderRadius: 6,
                          background: '#f0f4ff', color: '#3b5998', fontSize: 11, fontWeight: 700,
                          border: '1px solid #c7d2fe'
                        }}>
                          {emp.role || '—'}
                        </span>
                      </td>
                      {typeFilter === 'Worker' && (
                        <td style={{ fontWeight: 600, color: '#0f172a' }}>₹{emp.wage}/day</td>
                      )}
                      {typeFilter === 'Worker' && (
                        <td>
                          {emp.rating ? <StarRating value={emp.rating} /> : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                      )}
                      {typeFilter === 'Staff' && (
                        <td style={{ fontSize: 12, color: '#64748b' }}>{emp.email || '—'}</td>
                      )}
                      <td>
                        <span className={`status-badge ${emp.status === 'Active' ? 'status-active' : 'status-inactive'}`}>
                          {emp.status}
                        </span>
                      </td>
                      <td style={{ color: '#64748b', fontSize: 13 }}>{emp.joiningDate || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                          {typeFilter === 'Worker' ? (
                            <>
                              <button
                                className="btn-icon"
                                title="View worker profile"
                                style={{ background: '#f0fdf4', color: '#16a34a' }}
                                onClick={() => handleOpenView(emp)}
                              >
                                <Eye size={15} />
                              </button>
                              {canManageWorkers && (
                                  <button
                                    className="btn-icon"
                                    title="Edit worker details"
                                  style={{ background: '#eff6ff', color: '#3b82f6' }}
                                  onClick={() => handleOpenEdit(emp)}
                                >
                                  <Edit3 size={15} />
                                </button>
                              )}
                              {canManageWorkers && (
                                  <button
                                    className="btn-icon"
                                    title="Remove this worker"
                                  style={{ background: '#fef2f2', color: '#ef4444' }}
                                  onClick={() => handleDeleteClick(emp)}
                                >
                                  <Trash2 size={15} />
                                </button>
                              )}
                            </>
                          ) : (
                            <button
                              className="btn-icon"
                              title="View staff profile"
                              style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }}
                              onClick={() => handleOpenView(emp)}
                            >
                              <Eye size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Add / Edit Worker Modal ──────────────────────────────────── */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingEmp ? `Edit Worker — ${editingEmp.firstName}` : 'Add New Worker'}
        maxWidth="1080px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" icon={saving ? null : Save} onClick={handleSave} disabled={saving}>
              {saving ? <><BtnSpinner /> Saving…</> : 'Save Record'}
            </Button>
          </>
        }
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
          padding: 14,
          borderRadius: 12,
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          marginBottom: 18
        }}>
          <div>
            <div className="form-label">Worker Code</div>
            <div style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
              {editingEmp ? (formData.workerCode || editingEmp.id) : 'Auto generated on save'}
            </div>
          </div>
          <div>
            <div className="form-label">Branch</div>
            <div style={{ fontWeight: 600, color: '#334155' }}>
              {resolveBranchNameForDepartment(formData.dept) || 'Select department first'}
            </div>
          </div>
          <div>
            <div className="form-label">Status</div>
            <div style={{ fontWeight: 600, color: '#334155' }}>{formData.status || 'Active'}</div>
          </div>
          <div>
            <div className="form-label">Worker Role</div>
            <div style={{ fontWeight: 600, color: '#334155' }}>{formData.role || 'Not set yet'}</div>
          </div>
        </div>
        {/* 1. Basic Info */}
        <div className="form-section-title"><User size={16} /> 1. Basic Information</div>
        <div className="form-grid">
          <div>
            <label className="form-label">First Name *</label>
            <input className="form-control" value={formData.firstName} onChange={e => set('firstName', e.target.value)} placeholder="e.g. Raju" />
          </div>
          <div>
            <label className="form-label">Last Name</label>
            <input className="form-control" value={formData.lastName} onChange={e => set('lastName', e.target.value)} placeholder="e.g. Kumar" />
          </div>
          <div>
            <label className="form-label">Phone Number *</label>
            <input type="tel" className="form-control" value={formData.phone} onChange={e => set('phone', e.target.value)} placeholder="10-digit mobile number" />
          </div>
          <div>
            <label className="form-label">Daily Wage (₹)</label>
            <input type="number" className="form-control" value={formData.wage} onChange={e => set('wage', e.target.value)} placeholder="e.g. 650" min="0" />
          </div>
          <div>
            <label className="form-label">Worker Role / Work Type</label>
            <select className="form-control" value={formData.role} onChange={e => set('role', e.target.value)}>
              <option value="">Select worker role</option>
              {workerRoleOptions.map((option) => (
                <option key={`role-${option.value}`} value={option.value}>
                  {option.label || option.value}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 2. Job Details */}
        <div className="form-section-title"><Briefcase size={16} /> 2. Job Details</div>
        <div className="form-grid">
          <div>
            <label className="form-label">Department *</label>
            <select className="form-control" value={formData.dept} onChange={e => {
              const nextDept = e.target.value;
              setFormData((prev) => ({ ...prev, dept: nextDept, branch: resolveBranchIdForDepartment(nextDept) }));
            }}>
              {departmentOptions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Employment Status</label>
            <select className="form-control" value={formData.status} onChange={e => set('status', e.target.value)}>
              <option value="Active">Active — Currently working</option>
              <option value="Inactive">Inactive — Not working</option>
            </select>
          </div>
          <div>
            <label className="form-label">Branch</label>
            <select className="form-control" value={formData.branch} onChange={e => set('branch', e.target.value)}>
              {branchOptions.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Experience (years)</label>
            <input type="number" step="0.5" min="0" className="form-control" value={formData.experience} onChange={e => set('experience', e.target.value)} placeholder="e.g. 2" />
          </div>
        </div>

        {/* 3. Rating */}
        <div className="form-section-title"><Award size={16} /> 3. Performance Rating</div>
        <div className="form-grid">
          <div>
            <label className="form-label">Rating (0 – 5)  <span style={{ color: '#94a3b8' }}>Higher = better worker</span></label>
            <input
              type="number" step="0.5" min="0" max="5"
              className="form-control" placeholder="e.g. 4.0"
              value={formData.rating}
              onChange={e => set('rating', e.target.value)}
            />
          </div>
          <div>
            <label className="form-label">Skills</label>
            <input className="form-control" value={formData.skills} onChange={e => set('skills', e.target.value)} placeholder="e.g. Fast finishing, casting support, stone setting" />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label className="form-label">Remarks / Notes</label>
            <textarea className="form-control" rows={3} value={formData.remarks} onChange={e => set('remarks', e.target.value)} placeholder="Worker strengths, stage expertise, onboarding notes, or any special requirement" />
          </div>
        </div>

        {/* 4. Documents (create only) */}
        {!editingEmp && (
          <>
            <div className="form-section-title"><FileText size={16} /> 4. Identity Document</div>
            <div className="form-grid">
              <div>
                <label className="form-label">Document Type</label>
                <select className="form-control" value={formData.document.type} onChange={e => setDoc('type', e.target.value)}>
                  <option value="">— Select type —</option>
                  <option value="aadhaar">Aadhaar Card</option>
                  <option value="pan">PAN Card</option>
                  <option value="passport">Passport</option>
                </select>
              </div>
              <div>
                <label className="form-label">Name on Document</label>
                <input className="form-control" value={formData.document.nameOnDoc} onChange={e => setDoc('nameOnDoc', e.target.value)} placeholder="As printed on document" />
              </div>
              <div>
                <label className="form-label">Document Number</label>
                <input className="form-control" value={formData.document.number} onChange={e => setDoc('number', e.target.value)} placeholder="e.g. XXXX XXXX XXXX" />
              </div>
              <div style={{
                border: '2px dashed #e2e8f0', padding: 18, borderRadius: 10,
                textAlign: 'center', background: '#f8fafc', cursor: 'pointer'
              }}>
                <UploadCloud size={28} color="#94a3b8" style={{ marginBottom: 6 }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>Upload Document Scan</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>Aadhaar / PAN / Passport</div>
                <input type="file" accept="image/*,.pdf" onChange={e => setSelectedFile(e.target.files[0])} style={{ fontSize: 12 }} />
                {selectedFile && <div style={{ fontSize: 12, color: '#059669', marginTop: 6 }}>✓ {selectedFile.name}</div>}
              </div>
            </div>
          </>
        )}

        {/* 5. Bank Details */}
        <div className="form-section-title"><CreditCard size={16} /> {editingEmp ? '4' : '5'}. Bank Details</div>
        <div className="form-grid">
          <div>
            <label className="form-label">Account Holder Name</label>
            <input className="form-control" value={formData.bank.holder} onChange={e => setBank('holder', e.target.value)} placeholder="As per bank records" />
          </div>
          <div>
            <label className="form-label">Account Number</label>
            <input className="form-control" value={formData.bank.acc} onChange={e => setBank('acc', e.target.value)} placeholder="Bank account number" />
          </div>
          <div>
            <label className="form-label">IFSC Code</label>
            <input className="form-control" value={formData.bank.ifsc} onChange={e => setBank('ifsc', e.target.value)} placeholder="e.g. SBIN0001234" />
          </div>
          <div>
            <label className="form-label">Bank Name</label>
            <input className="form-control" value={formData.bank.bank} onChange={e => setBank('bank', e.target.value)} placeholder="e.g. State Bank of India" />
          </div>
          <div>
            <label className="form-label">Bank Branch</label>
            <input className="form-control" value={formData.bank.branch} onChange={e => setBank('branch', e.target.value)} placeholder="e.g. Head Office Branch" />
          </div>
          <div>
            <label className="form-label">Account Type</label>
            <select className="form-control" value={formData.bank.accountType} onChange={e => setBank('accountType', e.target.value)}>
              <option value="">— Select —</option>
              <option value="savings">Savings Account</option>
              <option value="current">Current Account</option>
            </select>
          </div>
        </div>

        {/* 6. Address */}
        <div className="form-section-title">{editingEmp ? '5' : '6'}. Home Address</div>
        <div className="form-grid">
          <div style={{ gridColumn: 'span 2' }}>
            <input className="form-control" placeholder="Address Line 1 (House / Street)" value={formData.address.line1} onChange={e => setAddr('line1', e.target.value)} />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <input className="form-control" placeholder="Address Line 2 (Area / Landmark)" value={formData.address.line2} onChange={e => setAddr('line2', e.target.value)} />
          </div>
          <input className="form-control" placeholder="City" value={formData.address.city} onChange={e => setAddr('city', e.target.value)} />
          <input className="form-control" placeholder="State" value={formData.address.state} onChange={e => setAddr('state', e.target.value)} />
          <input className="form-control" placeholder="Pincode" value={formData.address.pincode} onChange={e => setAddr('pincode', e.target.value)} />
        </div>
      </Modal>

      {/* ── View Worker / Staff Modal ─────────────────────────────── */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title={viewingEmp?._viewType === 'worker'
          ? `Worker Profile — ${viewingEmp?.firstName || ''} ${viewingEmp?.lastName || ''}`
          : `Staff Profile — ${viewingEmp?.name || `${viewingEmp?.firstName || ''} ${viewingEmp?.lastName || ''}`}`}
        maxWidth="960px"
        footer={<Button variant="secondary" onClick={() => setIsViewModalOpen(false)}>Close</Button>}
      >
        {viewingEmp && viewingEmp._viewType === 'staff' && (
          <div>
            <div style={{ display: 'flex', gap: 4, marginBottom: 18, background: '#f1f5f9', padding: 3, borderRadius: 10 }}>
              {[['overview', 'Overview'], ['documents', 'Documents']].map(([key, label]) => (
                <button key={key} onClick={() => setStaffViewTab(key)} style={{
                  flex: 1, padding: '8px 12px', border: 'none', borderRadius: 8,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                  background: staffViewTab === key ? 'white' : 'transparent',
                  color: staffViewTab === key ? '#1e293b' : '#64748b',
                  boxShadow: staffViewTab === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none'
                }}>{label}</button>
              ))}
            </div>

            {staffViewTab === 'overview' && (
              <div className="form-grid">
                <div><label className="form-label">Full Name</label><div style={{ fontWeight: 700, fontSize: 15 }}>{viewingEmp.name || `${viewingEmp.firstName} ${viewingEmp.lastName}`}</div></div>
                <div><label className="form-label">Email Address</label><div>{viewingEmp.email || '-'}</div></div>
                <div><label className="form-label">Role</label><div>
                  <span style={{ display: 'inline-flex', padding: '3px 10px', borderRadius: 6, background: '#f0f4ff', color: '#3b5998', fontSize: 12, fontWeight: 700, border: '1px solid #c7d2fe' }}>
                    {viewingEmp.role || 'UNASSIGNED'}
                  </span>
                  {viewingEmp.roles && viewingEmp.roles.length > 1 && (
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>
                      +{viewingEmp.roles.length - 1} more
                    </span>
                  )}
                </div></div>
                <div><label className="form-label">Branch</label><div>{viewingEmp.branch_name || '-'}</div></div>
                <div><label className="form-label">Phone</label><div>{viewingEmp.phone || '-'}</div></div>
                <div><label className="form-label">Account Status</label>
                  <span className={`status-badge ${viewingEmp.status === 'Active' ? 'status-active' : 'status-inactive'}`}>{viewingEmp.status}</span>
                </div>
                {viewingEmp.roles && viewingEmp.roles.length > 1 && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">All Roles</label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {viewingEmp.roles.map(r => (
                        <span key={r} style={{ display: 'inline-flex', padding: '3px 10px', borderRadius: 6, background: '#f0f4ff', color: '#3b5998', fontSize: 11, fontWeight: 700, border: '1px solid #c7d2fe' }}>
                          {r}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {staffViewTab === 'documents' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ fontSize: 13, color: '#64748b' }}>
                    Upload and track employee documents with expiry alerts.
                  </div>
                  <Button
                    variant="primary"
                    icon={UploadCloud}
                    onClick={() => {
                      resetDocForm();
                      setIsUploadDocModalOpen(true);
                    }}
                  >
                    Upload Document
                  </Button>
                </div>

                {documentsLoading ? (
                  <Spinner size="sm" label="Loading documents..." />
                ) : documents.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '22px 12px', border: '1px dashed #cbd5e1', borderRadius: 10, color: '#94a3b8' }}>
                    No documents uploaded yet.
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>File Name</th>
                          <th>Issue Date</th>
                          <th>Expiry Date</th>
                          <th>Status</th>
                          <th>Download</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map(doc => {
                          const badge = getExpiryBadge(doc.expiry_status);
                          return (
                            <tr key={doc.id}>
                              <td>{doc.document_type || 'OTHER'}</td>
                              <td style={{ fontSize: 12 }}>{doc.original_filename || 'file'}</td>
                              <td>{doc.issue_date || '-'}</td>
                              <td>{doc.expiry_date || '-'}</td>
                              <td>
                                <span style={{
                                  display: 'inline-flex', padding: '4px 10px', borderRadius: 999,
                                  background: badge.bg, color: badge.color, fontSize: 11, fontWeight: 700
                                }}>
                                  {badge.label}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn-icon"
                                  style={{ background: '#eff6ff', color: '#2563eb' }}
                                  onClick={() => handleDownloadDocument(doc)}
                                  title="Download document"
                                >
                                  <Download size={14} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {viewingEmp && viewingEmp._viewType === 'worker' && (
          <div>
            {/* Tab navigation */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#f1f5f9', padding: 3, borderRadius: 10 }}>
              {[['overview', 'Overview'], ['payslips', 'Payslips'], ['stages', 'Assigned Stages']].map(([key, label]) => (
                <button key={key} onClick={() => setViewTab(key)} style={{
                  flex: 1, padding: '8px 12px', border: 'none', borderRadius: 8,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                  background: viewTab === key ? 'white' : 'transparent',
                  color: viewTab === key ? '#1e293b' : '#64748b',
                  boxShadow: viewTab === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none'
                }}>{label}</button>
              ))}
            </div>

            {viewLoading ? (
              <Spinner label="Loading worker details…" />
            ) : (
              <>
                {/* ── Overview Tab ──────────────────────── */}
                {viewTab === 'overview' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* Basic Info */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
                      <div><label className="form-label">Name</label><div style={{ fontWeight: 700 }}>{viewingEmp.firstName} {viewingEmp.lastName}</div></div>
                      <div><label className="form-label">Phone</label><div>{viewingEmp.phone || '—'}</div></div>
                      <div><label className="form-label">Daily Wage</label><div style={{ fontWeight: 600 }}>₹{viewingEmp.wage}/day</div></div>
                      <div><label className="form-label">Status</label>
                        <span className={`status-badge ${viewingEmp.status === 'Active' ? 'status-active' : 'status-inactive'}`}>{viewingEmp.status}</span>
                      </div>
                    </div>

                    {/* Performance Stars */}
                    <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Award size={15} /> Performance Rating
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                        <StarRating value={workerPerf?.stars || viewingEmp.rating || 0} />
                        <span style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{workerPerf?.stars || viewingEmp.rating || 0}/5</span>
                        {workerPerf?.bonus_eligible && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 12px',
                            borderRadius: 20, background: '#dcfce7', color: '#15803d', fontSize: 12, fontWeight: 700
                          }}>
                            <CheckCircle size={13} /> Bonus Eligible
                          </span>
                        )}
                        {workerPerf?.last_evaluated && (
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>Last evaluated: {new Date(workerPerf.last_evaluated).toLocaleDateString()}</span>
                        )}
                      </div>
                      {workerPerf?.remarks && <div style={{ marginTop: 8, fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>"{workerPerf.remarks}"</div>}
                    </div>

                    <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Award size={15} /> 12-Month Rating Trend
                      </div>
                      {ratingHistoryLoading ? (
                        <Spinner size="sm" label="Loading rating history..." />
                      ) : workerRatingHistory.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#94a3b8' }}>No monthly ratings available yet.</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div style={{ width: '100%', height: 220 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                data={toRatingHistorySeries(workerRatingHistory)}
                                margin={{ top: 8, right: 8, left: -18, bottom: 0 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                <YAxis domain={[0, 5]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                <Tooltip
                                  formatter={(value, name) => [Number(value).toFixed(2), name === 'stars' ? 'Stars' : 'Score']}
                                  contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }}
                                />
                                <Line type="monotone" dataKey="stars" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            Latest rating: <strong style={{ color: '#0f172a' }}>{Number(workerRatingHistory[workerRatingHistory.length - 1]?.stars || 0).toFixed(2)}</strong> / 5
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Leave Balance */}
                    <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FileText size={15} /> Leave Balance
                      </div>
                      {workerLeave.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#94a3b8' }}>No leave types configured</div>
                      ) : (
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          {workerLeave.map(lb => (
                            <div key={lb.code} style={{
                              background: 'white', borderRadius: 8, padding: '10px 16px',
                              border: '1px solid #e2e8f0', minWidth: 110, textAlign: 'center'
                            }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{lb.code}</div>
                              <div style={{ fontSize: 18, fontWeight: 800, color: lb.remaining > 0 ? '#059669' : '#ef4444', margin: '4px 0' }}>
                                {lb.remaining}<span style={{ fontSize: 12, color: '#94a3b8' }}>/{lb.allocated}</span>
                              </div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>{lb.used} used</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Payslips Tab ──────────────────────── */}
                {viewTab === 'payslips' && (
                  <div>
                    {workerPayslips.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                        <CreditCard size={32} style={{ marginBottom: 8, color: '#e2e8f0' }} />
                        <div style={{ fontSize: 14, fontWeight: 600 }}>No payslip records found</div>
                      </div>
                    ) : (
                      <div className="table-wrapper">
                        <table>
                          <thead>
                            <tr>
                              <th>Period</th><th>Gross</th><th>Deductions</th><th>Net Pay</th><th>Status</th><th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {workerPayslips.map(ps => (
                              <tr key={ps.id}>
                                <td style={{ fontSize: 12 }}>{ps.period}</td>
                                <td style={{ fontWeight: 600 }}>₹{ps.gross.toLocaleString()}</td>
                                <td style={{ color: '#ef4444' }}>₹{ps.deductions.toLocaleString()}</td>
                                <td style={{ fontWeight: 700, color: '#059669' }}>₹{ps.net_pay.toLocaleString()}</td>
                                <td>
                                  <span className={`status-badge ${ps.status === 'paid' ? 'status-active' : 'status-inactive'}`}>
                                    {ps.status}
                                  </span>
                                </td>
                                <td>
                                  <button className="btn-icon" title="Download payslip" style={{ background: '#eff6ff', color: '#3b82f6' }}
                                    onClick={() => {
                                      const w = window.open('', '_blank', 'width=800,height=600');
                                      w.document.write(`<html><head><title>Payslip — ${viewingEmp.firstName} ${viewingEmp.lastName}</title>
                                        <style>body{font-family:Arial,sans-serif;padding:40px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f1f5f9}.header{text-align:center;margin-bottom:20px}h2{margin:0}.meta{display:flex;justify-content:space-between;margin:16px 0}</style></head><body>
                                        <div class="header"><h2>PAYSLIP</h2><p>${ps.period}</p></div>
                                        <div class="meta"><div><strong>Employee:</strong> ${viewingEmp.firstName} ${viewingEmp.lastName}</div><div><strong>ID:</strong> ${viewingEmp.id}</div></div>
                                        <table><tr><th>Component</th><th>Amount</th></tr>
                                        <tr><td>Gross Salary</td><td>₹${ps.gross.toLocaleString()}</td></tr>
                                        <tr><td>Total Deductions</td><td>₹${ps.deductions.toLocaleString()}</td></tr>
                                        <tr style="font-weight:bold;background:#f0fdf4"><td>Net Pay</td><td>₹${ps.net_pay.toLocaleString()}</td></tr>
                                        </table><script>setTimeout(()=>window.print(),300)</script></body></html>`);
                                      w.document.close();
                                    }}
                                  >
                                    <Download size={14} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Stages Tab ────────────────────────── */}
                {viewTab === 'stages' && (
                  <div>
                    {workerStages.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                        <Layers size={32} style={{ marginBottom: 8, color: '#e2e8f0' }} />
                        <div style={{ fontSize: 14, fontWeight: 600 }}>No active stages assigned</div>
                      </div>
                    ) : (
                      <div className="table-wrapper">
                        <table>
                          <thead>
                            <tr><th>Stage</th><th>Code</th><th>Job Card</th><th>Status</th><th>Started</th></tr>
                          </thead>
                          <tbody>
                            {workerStages.map(s => (
                              <tr key={s.id}>
                                <td style={{ fontWeight: 600 }}>{s.stage_name}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{s.stage_code}</td>
                                <td>#{s.job_card_id}</td>
                                <td><span className="status-badge status-active">{s.status}</span></td>
                                <td style={{ fontSize: 12, color: '#64748b' }}>{s.started_at ? new Date(s.started_at).toLocaleDateString() : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ── Delete Confirmation Modal ────────────────────────────────── */}
      <Modal
        isOpen={isUploadDocModalOpen}
        onClose={() => setIsUploadDocModalOpen(false)}
        title="Upload Employee Document"
        maxWidth="620px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsUploadDocModalOpen(false)} disabled={uploadingDoc}>Cancel</Button>
            <Button variant="primary" icon={uploadingDoc ? null : Save} onClick={handleUploadDocument} disabled={uploadingDoc}>
              {uploadingDoc ? <><BtnSpinner /> Uploading...</> : 'Upload'}
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <div>
            <label className="form-label">Document Type</label>
            <select className="form-control" value={docForm.document_type} onChange={(e) => setDocForm(p => ({ ...p, document_type: e.target.value }))}>
              {docTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Issue Date</label>
            <input type="date" className="form-control" value={docForm.issue_date} onChange={(e) => setDocForm(p => ({ ...p, issue_date: e.target.value }))} />
          </div>
          <div>
            <label className="form-label">Expiry Date (optional)</label>
            <input type="date" className="form-control" value={docForm.expiry_date} onChange={(e) => setDocForm(p => ({ ...p, expiry_date: e.target.value }))} />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label className="form-label">Notes</label>
            <textarea className="form-control" value={docForm.notes} onChange={(e) => setDocForm(p => ({ ...p, notes: e.target.value }))} rows={3} />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label className="form-label">File (PDF/Image)</label>
            <input
              type="file"
              accept=".pdf,image/*"
              className="form-control"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setDocForm(p => ({ ...p, file: f }));
                if (docPreviewUrl) URL.revokeObjectURL(docPreviewUrl);
                if (f && String(f.type || '').startsWith('image/')) {
                  setDocPreviewUrl(URL.createObjectURL(f));
                } else {
                  setDocPreviewUrl('');
                }
              }}
            />
          </div>
          {docPreviewUrl && (
            <div style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Image Preview</label>
              <img src={docPreviewUrl} alt="document preview" style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, border: '1px solid #e2e8f0' }} />
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}
        title="Remove Worker"
        maxWidth="460px"
        footer={
          <div style={{ display: 'flex', gap: 10, width: '100%' }}>
            <Button variant="secondary" onClick={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}>
              Cancel
            </Button>
            <button
              onClick={() => handleDeleteConfirm(false)}
              style={{
                flex: 1, padding: '9px 16px', borderRadius: 8, border: 'none',
                background: '#fef2f2', color: '#dc2626', fontWeight: 700, fontSize: 13, cursor: 'pointer'
              }}
              title="Permanently removes the worker from the database"
            >
              Permanent Delete
            </button>
            <button
              onClick={() => handleDeleteConfirm(true)}
              style={{
                flex: 1, padding: '9px 16px', borderRadius: 8, border: 'none',
                background: '#fffbeb', color: '#d97706', fontWeight: 700, fontSize: 13, cursor: 'pointer'
              }}
              title="Keeps records but marks worker as Inactive"
            >
              Mark Inactive
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '4px 0' }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AlertTriangle size={22} color="#ef4444" />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 6 }}>
              Remove {deleteTarget?.firstName} {deleteTarget?.lastName}?
            </div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
              Choose an option below:
              <ul style={{ margin: '8px 0 0 16px', padding: 0, color: '#475569' }}>
                <li style={{ marginBottom: 4 }}><strong>Mark Inactive</strong> — keeps all history, just hides from active lists (recommended)</li>
                <li><strong>Permanent Delete</strong> — removes completely; only works if the worker has no attendance or payroll records</li>
              </ul>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Employees;


